import { STANDALONE_CONSTANTS } from '@/pages/StandAlone/constants';
import { useState, useEffect } from 'react';

export interface StandaloneAuthParams {
  sid?: string | null;
  sig?: string | null;
}

export interface StandaloneAuthResult {
  isLoading: boolean;
  isValidated: boolean;
  error: string | null;
  sid: string | null;
  sig: string | null;
}

/**
 * Custom hook for validating standalone authentication parameters.
 * Checks sid, sig from sessionStorage/props and validates expiry.
 *
 * @param props - Optional props containing sid, sig from URL params
 * @returns {StandaloneAuthResult} - Validation state and credentials
 */
export const useStandaloneAuth = (
  props?: StandaloneAuthParams,
): StandaloneAuthResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [isValidated, setIsValidated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get values from sessionStorage or props (keys match standalone.tsx)
  const storedSid = props?.sid || null;
  const storedSig = props?.sig || null;

  useEffect(() => {
    const validate = () => {
      // Check required params
      if (!storedSid || !storedSig) {
        setError(STANDALONE_CONSTANTS.SESSION_EXPIRED_MODAL.DESCRIPTION);
        setIsLoading(false);
        setIsValidated(false);
      } else {
        // Validation successful
        setIsValidated(true);
        setError(null);
        setIsLoading(false);
      }
    };

    validate();
  }, [storedSid, storedSig]);

  return {
    isLoading,
    isValidated,
    error,
    sid: storedSid,
    sig: storedSig,
  };
};

export default useStandaloneAuth;
